import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'
import galaxyVertexShader from './shaders/galaxy/vertex.glsl'
import galaxyFragmentShader from './shaders/galaxy/fragment.glsl'

/**
 * Loader
 */
const loaderEl = document.getElementById('loader')
const loaderLine = document.getElementById('loader-line')
const progressText = document.getElementById('progress-text')

let loadProgress = 0

const updateProgress = (value) => {
    loadProgress = Math.min(value, 99)
    progressText.textContent = Math.floor(loadProgress)
    loaderLine.style.setProperty('--progress', loadProgress + '%')
}

const hideLoader = () => {
    setTimeout(() => {
        progressText.textContent = '100'
        loaderLine.style.setProperty('--progress', '100%')
        setTimeout(() => {
            loaderEl.classList.add('hidden')
        }, 400)
    }, 1000)
}

/**
 * Base
 */
const gui = new GUI()
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

/**
 * Galaxy Parameters
 */
const parameters = {
    count: 336300,
    size: 0.005,
    radius: 8.94,
    branches: 19,
    spin: 1,
    randomness: 0.378,
    randomnessPower: 8.783,
    insideColor: '#f4fde3',
    outsideColor: '#0048ff'
}

let geometry = null
let material = null
let points = null

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: window.devicePixelRatio < 2,
    powerPreference: "high-performance"
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

updateProgress(20)

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.set(3, 3, 3)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.05
controls.enablePan = false
controls.minDistance = 2
controls.maxDistance = 20
controls.minPolarAngle = 0.5
controls.maxPolarAngle = Math.PI - 0.5

updateProgress(40)

/**
 * Background Stars
 */
const createBackgroundStars = () => {
    const starsGeometry = new THREE.BufferGeometry()
    const starsCount = 2000
    const positions = new Float32Array(starsCount * 3)

    for (let i = 0; i < starsCount; i++) {
        const i3 = i * 3
        const radius = 40 + Math.random() * 40
        const theta = Math.random() * Math.PI * 2
        const phi = Math.acos(2 * Math.random() - 1)

        positions[i3] = radius * Math.sin(phi) * Math.cos(theta)
        positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
        positions[i3 + 2] = radius * Math.cos(phi)
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

    const starsMaterial = new THREE.PointsMaterial({
        size: 0.08,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.8,
        color: 0xffffff,
        blending: THREE.AdditiveBlending,
        depthWrite: false
    })

    const stars = new THREE.Points(starsGeometry, starsMaterial)
    scene.add(stars)
    return stars
}

updateProgress(60)

/**
 * Generate Galaxy
 */
const generateGalaxy = () => {
    if (points !== null) {
        geometry.dispose()
        material.dispose()
        scene.remove(points)
    }

    geometry = new THREE.BufferGeometry()

    const positions = new Float32Array(parameters.count * 3)
    const colors = new Float32Array(parameters.count * 3)
    const scales = new Float32Array(parameters.count)
    const randomness = new Float32Array(parameters.count * 3)

    const insideColor = new THREE.Color(parameters.insideColor)
    const outsideColor = new THREE.Color(parameters.outsideColor)

    for (let i = 0; i < parameters.count; i++) {
        const i3 = i * 3
        const radius = Math.random() * parameters.radius
        const branchAngle = (i % parameters.branches) / parameters.branches * Math.PI * 2

        positions[i3] = Math.cos(branchAngle) * radius
        positions[i3 + 1] = 0
        positions[i3 + 2] = Math.sin(branchAngle) * radius

        const randomPow = Math.pow(Math.random(), parameters.randomnessPower)
        const sign1 = Math.random() < 0.5 ? 1 : -1
        const sign2 = Math.random() < 0.5 ? 1 : -1
        const sign3 = Math.random() < 0.5 ? 1 : -1

        randomness[i3] = randomPow * sign1 * parameters.randomness * radius
        randomness[i3 + 1] = randomPow * sign2 * parameters.randomness * radius
        randomness[i3 + 2] = randomPow * sign3 * parameters.randomness * radius

        const mixedColor = insideColor.clone()
        mixedColor.lerp(outsideColor, radius / parameters.radius)

        colors[i3] = mixedColor.r
        colors[i3 + 1] = mixedColor.g
        colors[i3 + 2] = mixedColor.b

        scales[i] = Math.random()
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geometry.setAttribute('aScale', new THREE.BufferAttribute(scales, 1))
    geometry.setAttribute('aRandomness', new THREE.BufferAttribute(randomness, 3))

    material = new THREE.ShaderMaterial({
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexColors: true,
        vertexShader: galaxyVertexShader,
        fragmentShader: galaxyFragmentShader,
        uniforms: {
            uTime: { value: 0 },
            uSize: { value: 30 * renderer.getPixelRatio() }
        }
    })

    points = new THREE.Points(geometry, material)
    scene.add(points)
}

updateProgress(80)

/**
 * GUI
 */
const galaxyFolder = gui.addFolder('Galaxy')
galaxyFolder.add(parameters, 'count').min(100).max(1000000).step(100).onFinishChange(generateGalaxy)
galaxyFolder.add(parameters, 'radius').min(0.01).max(20).step(0.01).onFinishChange(generateGalaxy)
galaxyFolder.add(parameters, 'branches').min(2).max(20).step(1).onFinishChange(generateGalaxy)
galaxyFolder.add(parameters, 'randomness').min(0).max(2).step(0.001).onFinishChange(generateGalaxy)
galaxyFolder.add(parameters, 'randomnessPower').min(1).max(10).step(0.001).onFinishChange(generateGalaxy)
galaxyFolder.addColor(parameters, 'insideColor').onFinishChange(generateGalaxy)
galaxyFolder.addColor(parameters, 'outsideColor').onFinishChange(generateGalaxy)


/**
 * Resize
 */
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Initialize
 */
generateGalaxy()
const stars = createBackgroundStars()

updateProgress(99)
hideLoader()

/**
 * Animation
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    material.uniforms.uTime.value = elapsedTime
    stars.rotation.y = elapsedTime * 0.01

    controls.update()
    renderer.render(scene, camera)

    requestAnimationFrame(tick)
}

tick()
